import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Sparkles, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trackSearch } from "@/lib/analytics";

export default function LiveSearch({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", {
          params: { search: q, limit: 8 },
          signal: controller.signal,
        });
        setResults(data);
      } catch (e) {
        // ignore aborts
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    trackSearch(query.trim());
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-obsidian/40 backdrop-blur-sm z-[55]"
            data-testid="livesearch-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 inset-x-0 z-[56] bg-bone shadow-xl"
            data-testid="livesearch-panel"
          >
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-6">
              <form onSubmit={submit} className="flex items-center gap-4 border-b border-obsidian/20 pb-3">
                <Search className="w-5 h-5 text-neutral-500" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products, brands, or rituals…"
                  data-testid="livesearch-input"
                  className="flex-1 bg-transparent font-serif text-2xl md:text-3xl placeholder:text-neutral-300 focus:outline-none tracking-tight"
                />
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="livesearch-close"
                  className="p-1 hover:bg-black/5 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </form>

              <div className="mt-6 min-h-[80px] pb-6">
                {query.trim().length < 2 && (
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-sans mb-4">
                      Popular searches
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["hyaluronic serum", "matte lipstick", "aloe vera", "SPF 50", "clean fragrance", "hair mask"].map((s) => (
                        <button
                          key={s}
                          onClick={() => setQuery(s)}
                          data-testid={`livesearch-suggestion-${s}`}
                          className="text-xs font-sans px-3 py-1.5 border border-obsidian/15 hover:border-obsidian transition-colors"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {query.trim().length >= 2 && loading && (
                  <p className="font-serif italic text-neutral-500">Searching…</p>
                )}

                {query.trim().length >= 2 && !loading && results.length === 0 && (
                  <p className="font-serif italic text-neutral-500" data-testid="livesearch-empty">
                    Nothing matches "{query}". Try a broader term.
                  </p>
                )}

                {results.length > 0 && (
                  <>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 font-sans mb-4">
                      {results.length} results
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {results.map((p) => (
                        <Link
                          key={p.id}
                          to={`/product/${p.id}`}
                          onClick={onClose}
                          data-testid={`livesearch-result-${p.id}`}
                          className="flex items-center gap-4 p-2 hover:bg-black/5 transition-colors group"
                        >
                          <div className="w-14 h-16 bg-neutral-100 overflow-hidden flex-shrink-0">
                            {p.image && (
                              <img
                                src={p.image}
                                alt=""
                                className="w-full h-full object-contain mix-blend-multiply p-1"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 font-sans">
                              {p.brand}
                            </p>
                            <p className="font-serif text-sm mt-0.5 leading-tight truncate group-hover:text-[#0A0A0A] transition-colors">
                              {p.name}
                            </p>
                            <p className="font-serif text-sm mt-1">${p.price?.toFixed?.(2) ?? p.price}</p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-obsidian transition-colors" />
                        </Link>
                      ))}
                    </div>
                    <button
                      onClick={submit}
                      data-testid="livesearch-see-all"
                      className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase font-sans font-medium border-b border-obsidian pb-1 hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
                    >
                      See all results <ArrowRight className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
